import { SetTitle } from '@abovo/webvis-data-access';
import { Injectable, Injector, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

/**
 * The single writer of the document title. Also stores the resolved title in the router state,
 * because route serialization happens before title resolvers have run.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
   private readonly title = inject(Title);
   private readonly store = inject(Store);
   private readonly injector = inject(Injector);

   private translate: TranslateService;
   private lastTitle: string | null = null;
   private watchingTranslationChanges = false;

   // set browser title
   override updateTitle(routerState: RouterStateSnapshot): void {
      const title = this.buildTitle(routerState);
      this.lastTitle = title ?? null;
      this.setDocumentTitle();
      this.watchTranslationChanges();
      // Also without a title, so a page without one never keeps the title of the page before it
      this.store.dispatch(new SetTitle({ title: title ?? '' }));
   }

   private setDocumentTitle(): void {
      const appTitle = this.getTranslateService().instant('application.title');
      if (this.lastTitle) {
         this.title.setTitle(`${appTitle} - ${this.getTranslateService().instant(this.lastTitle)}`);
      } else {
         this.title.setTitle(appTitle);
      }
   }

   /** Re-translate the document title when translations load or the language changes. */
   private watchTranslationChanges(): void {
      if (this.watchingTranslationChanges) {
         return;
      }
      this.watchingTranslationChanges = true;
      this.getTranslateService().onTranslationChange.subscribe(() => this.setDocumentTitle());
   }

   private getTranslateService(): TranslateService {
      if (!this.translate) {
         this.translate = this.injector.get(TranslateService);
      }
      return this.translate;
   }
}
